<template>
  <div v-if="rasterLayers.length > 0">
    <v-overlay :value="overlay">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-divider class="mb-4" />
    <div>
      <meteogram-button
        v-if="isMeteogram"
        :meteogram-enabled="meteogramEnabled"
        :location-name="map.locationName"
        :meteogram-obj="meteogramObj"
        :map-enabled="mapEnabled"
        @toggle-meteogram-btn="$emit('toggle-meteogram-btn', $event)"
        @toggle-map-btn="toggleMapBtn()"
      />
    </div>
    <div
      v-if="showSelect"
      class="mt-4"
    >
      <div class="d-flex">
        <strong>
          {{ $t('ServiceCard.rasterLayer') }}
        </strong>
        <v-spacer />
        <div class="d-flex">
          <layer-opacity-menu
            :key="map.renderOpacityMenu[serviceCardId]"
            :service-card-id="serviceCardId"
            :initial-opacity="getLayerInitialOpacity(selectedRasterLayer.layer_opacity)"
          />
          <v-btn
            v-if="$route.params.id !== 'Archive'"
            outlined
            icon
            small
            color="#505050"
            class="ml-2"
            :disabled="isRefreshing || refreshBtnDisabled"
            @click="clearRefreshInterval(serviceCardId); initRefresh(); setRefreshInterval()"
          >
            <v-icon
              :class="rotateEffect"
              color="#505050"
            >
              mdi-refresh
            </v-icon>
          </v-btn>
        </div>
      </div>
      <div
        v-if="$route.params.id === 'Real-Time' || $route.params.id === 'Forecast'"
        class="d-flex align-center mt-2"
      >
        <v-select
          ref="rasterSelect"
          v-model="selectedRasterLayer"
          :disabled="map.disableAnimationPlay || isRefreshing"
          :attach="`.card${serviceCardId}`"
          :items="rasterLayers"
          :item-text="getLayerLabel"
          item-color="RMprimary"
          color="RMprimary"
          return-object
          hide-details
          outlined
          :append-icon="isSelectVoid() ? 'mdi-menu-down' : 'mdi-close'"
          :class="[ `card${serviceCardId}`, !isSelectVoid() && 'no-rotate-icon']"
          @change="$emit('reset-min-max-step');removePreviousLayer(); openRasterLayer(false, selectedRasterLayer); googleTag();"
          @click:append="handleAppendIconClick()"
        />
      </div>
      <div
        v-else
        class="d-flex align-center mt-2"
      >
        <v-select
          ref="rasterSelect"
          v-model="selectedRasterLayer"
          :disabled="map.disableAnimationPlay"
          :attach="`.card${serviceCardId}`"
          :items="rasterLayers"
          :item-text="getLayerLabel"
          item-color="RMprimary"
          color="RMprimary"
          return-object
          hide-details
          outlined
          :append-icon="isSelectVoid() ? 'mdi-menu-down' : 'mdi-close'"
          :class="[ `card${serviceCardId}`, !isSelectVoid() && 'no-rotate-icon']"
          @click:append="handleAppendIconClick()"
        />
      </div>
      <div v-if="$route.params.id === 'Archive' && !overlay">
        <service-card-archive
          v-if="renderArchiveDate"
          :selected-raster-layer="selectedRasterLayer"
          :raster-layers="rasterLayers"
          :service-card-id="serviceCardId"
          :animation-limit-range-array="animationLimitRangeArray"
          :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
          :animation-step-date-type="animationStepDateType"
          :animation-step="animationStep"
          :min-step="minStep"
          :max-step="maxStep"
          :is-animation="isAnimation"
          :is-table="isTable"
          :is-graph="isGraph"
          :service-name="serviceName"
          :layer-type="'RASTER'"
          :animation-time-array="animationTimeArray"
          :animation-empty-steps="animationEmptySteps"
          :animation-start-index="animationStartIndex"
          :no-features-found="noFeaturesFound"
          :no-wmts-features-found="noWmtsFeaturesFound"
          :table-chart-range-date="tableChartRangeDate"
          @set-animation-steps="setAnimationSteps($event)"
          @closeAllOtherServices="$emit('closeAllOtherServices', $event)"
          @activatedNewArchiveService="$emit('activatedNewArchiveService')"
          @changedServiceLayer="$emit('changedServiceLayer')"
          @no-raster-features-found="noRasterFeaturesFound($event)"
          @closeRasterService="$emit('closeRasterService', serviceCardId)"
          @google-tag="googleTag()"
        />
      </div>
      <div v-if="$route.params.id === 'Forecast' && renderPlayer">
        <service-card-forecast
          :selected-raster-layer="selectedRasterLayer"
          :service-card-id="serviceCardId"
          :animation-limit-range-array="animationLimitRangeArray"
          :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
          :animation-step-date-type="animationStepDateType"
          :animation-step="animationStep"
          :min-step="minStep"
          :max-step="maxStep"
          :is-animation="isAnimation"
          :pause-animation="pauseAnimation"
          :overlay="overlay"
          :service-name="serviceName"
          :layer-type="'RASTER'"
          :animation-time-array="animationTimeArray"
          :animation-empty-steps="animationEmptySteps"
          :animation-start-index="animationStartIndex"
          :no-features-found="noFeaturesFound"
          :no-wmts-features-found="noWmtsFeaturesFound"
          :table-chart-range-date="tableChartRangeDate"
          :check-policy-limits="checkPolicyLimits"
          @set-animation-steps="setAnimationSteps($event)"
          @reset-min-max-step="$emit('reset-min-max-step')"
          @set-refresh-interval="setRefreshInterval()"
          @no-raster-features-found="noRasterFeaturesFound($event)"
        />
      </div>
      <div v-if="$route.params.id === 'Real-Time' && !mobileBreakpoints()">
        <animation-player
          v-if="renderPlayer"
          :selected-raster-layer="selectedRasterLayer"
          :service-card-id="serviceCardId"
          :animation-limit-range-array="animationLimitRangeArray"
          :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
          :animation-step-date-type="animationStepDateType"
          :animation-step="animationStep"
          :min-step="minStep"
          :max-step="maxStep"
          :is-animation="isAnimation"
          :pause-animation="pauseAnimation"
          :overlay="overlay"
          :service-name="serviceName"
          :layer-type="'RASTER'"
          :animation-time-array="animationTimeArray"
          :animation-empty-steps="animationEmptySteps"
          :animation-start-index="animationStartIndex"
          :no-features-found="noFeaturesFound"
          :no-wmts-features-found="noWmtsFeaturesFound"
          @no-raster-features-found="noRasterFeaturesFound($event)"
        />
      </div>
      <!-- <div v-if="($route.params.id === 'Forecast' || $route.params.id === 'Real-Time')">
        <raster-layer-table-chart-buttons
          v-if="isTable || isGraph"
          :raster-layers="rasterLayers"
          :selected-raster-layer="selectedRasterLayer"
          :is-table="isTable"
          :is-graph="isGraph"
          :is-animation="isAnimation"
          :service-name="serviceName"
          :table-chart-range-date="tableChartRangeDate"
          :is-daily-data="dailyData"
        />
      </div> -->
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { BINARY_BASE_URL } from '@/constants.js';
import { toLonLat } from 'ol/proj';
import { getFormattedDate, updatedDateWithTimeStep, timeToMilliseconds, dateUnit, formatLayerLabel } from '@/utils.js';

export default {
  components: {
    ServiceCardForecast: () => import('@/components/layout/ServiceCardForecast'),
    AnimationPlayer: () => import('@/components/utils/AnimationPlayer'),
    ServiceCardArchive: () => import('@/components/layout/ServiceCardArchive'),
    // RasterLayerTableChartButtons: () => import('@/components/utils/RasterLayerTableChartButtons'),
    MeteogramButton: () => import('@/components/utils/MeteogramButton'),
    LayerOpacityMenu: () => import('@/components/utils/LayerOpacityMenu.vue')
  },
  props: {
    serviceCardId: {
      type: String,
      default: ''
    },
    animationStepDateType: {
      type: String,
      default: ''
    },
    animationStep: {
      type: String,
      default: ''
    },
    animationLimitDateType: {
      type: String,
      default: ''
    },
    animationLimitFuture: {
      type: String,
      default: ''
    },
    animationLimitPast: {
      type: String,
      default: ''
    },
    refreshIntervalStep: {
      type: String,
      default: ''
    },
    refreshIntervalStepType: {
      type: String,
      default: ''
    },
    serviceName: {
      type: String,
      default: ''
    },
    isAnimation: {
      type: Boolean,
      default: false
    },
    isTable: {
      type: Boolean,
      default: false
    },
    isGraph: {
      type: Boolean,
      default: false
    },
    isMeteogram: {
      type: Boolean,
      default: false
    },
    meteogramEnabled: {
      type: Boolean,
      default: false
    },
    meteogramObj: {
      type: Object,
      default: () => {}
    },
    minStep: {
      type: Date,
      default: null
    },
    maxStep: {
      type: Date,
      default: null
    },
    rasterLayers: {
      type: Array,
      default: () => []
    },
    layerToSelectRaster: {
      type: Object,
      default: () => {}
    },
    animationLimitRangeArray: {
      type: Array,
      default: () => []
    },
    animationLimitRangeArrayPolicy: {
      type: Array,
      default: () => []
    },
    policyLimits: {
      type: Object,
      default: () => {}
    },
    checkPolicyLimits: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      overlay: false,
      renderArchiveDate: true,
      isRefreshing: false,
      refreshBtnDisabled: false,
      renderPlayer: false,
      noFeaturesFound: false,
      noWmtsFeaturesFound: false,
      mapEnabled: false,
      pauseAnimation: false,
      selectedRasterLayer: {},
      animationTimeArray: [],
      animationEmptySteps: [],
      animationStartIndex: 0,
      tableChartRangeDate: '',
      rotateEffect: '',
      runtime: ''
    };
  },
  computed: {
    ...mapState(['map', 'wsServices', 'app_user', 'geoserver_data', 'table_chart']),
    showSelect() {
      return this.mapEnabled || !this.isMeteogram;
    },
    dailyData() {
      return this.animationStepDateType === 'dynamic_months' || this.animationStepDateType === 'dynamic_days' || (this.animationStepDateType === 'dynamic_hours' && this.animationStep >= 24);
    },
    skipWmtsCalls() {
      return this.$route.params.id === 'Archive' && this.selectedRasterLayer.skip_wmts_calls;
    }
  },
  watch: {
    $route() {},
    layerToSelectRaster: {
      async handler(newValue) {
        if (newValue && Object.keys(newValue).length !== 0) {
          const isDefaultService = this.app_user.defaultService.GLOBAL?.service_type === 'WS' && this.selectedRasterLayer.service_id !== this.layerToSelectRaster.service_id && this.app_user.defaultService.GLOBAL?.service_details.some(item => item.service_id === this.serviceCardId);

          const fromRoute = this.$route.params.id.replace('-', '');
          const isSpecificDefaultService = this.app_user.defaultService[fromRoute]?.service_type && this.selectedRasterLayer.service_id !== this.layerToSelectRaster.service_id && this.app_user.defaultService[fromRoute].service_details.some(item => item.service_id === this.serviceCardId) && this.map.specificDefaultService[fromRoute];

          const isSingleLayer = this.rasterLayers.length === 1;
          const isAlredyActive = this.wsServices.activeServices.find(s => s.layer_name === newValue.layer_name && s.service_id === newValue.service_id);

          if (!isAlredyActive && (isDefaultService || isSpecificDefaultService || isSingleLayer)) {
            this.selectedRasterLayer = this.layerToSelectRaster;

            if (isSpecificDefaultService && this.app_user.defaultService[fromRoute].service_details.some(item => item.service_id === this.serviceCardId && item.is_meteogram)) {
              this.$emit('toggle-meteogram-btn', true);
              this.$emit('check-specific-default-service');
            }

            if (Object.keys(this.selectedRasterLayer).length !== 0 && !this.map.OLmap.getLayers().getArray().slice(1).some(layer => layer.values_.service_id === this.layerToSelectRaster.service_id) && this.app_user.defaultService.GLOBAL?.service_path !== 'map/Archive' && this.$route.params.id !== 'Archive') {
              this.mapEnabled = true;
              this.openRasterLayer(true, this.selectedRasterLayer);

              if (isSpecificDefaultService) {
                this.$emit('check-specific-default-service');
              }
            }
          }
        }
      },
      immediate: true
    }
  },
  destroyed() {
    this.$emit('toggle-meteogram-btn', false);
  },
  methods: {
    ...mapActions(['addActiveService', 'removeActiveService', 'loadRasterLayer', 'removeEveryRasterLayer', 'checkOpacityServiceID', 'resetAnimationTime', 'storeWsServices', 'getWmtsArray', 'getWmtsGDV', 'checkNewData', 'removeLayerByServiceID', 'getLayerExtent']),
    getLayerLabel(layer) {
      const component = layer.multi_variable_layer_configs?.find(item => item.target_udm) ||
        layer.multi_variable_layer_configs?.find(item => item.layer_name === layer.layer_name);

      return formatLayerLabel(
        this.$t(`${layer.ws_name}:${layer.layer_name}_serviceID:${this.serviceCardId}.label`),
        component?.udm || layer.udm
      );
    },
    async openRasterLayer(isDefaultService, raster) {
      if (!this.isRefreshing) {
        if (!(raster.service_id in this.map.refreshIntervalID)) {
          this.setRefreshInterval();
        }
      }

      this.geoserver_data.rasterLegend_selectedLabel = [];
      this.renderPlayer = false;
      this.$nextTick(() => {
        this.renderPlayer = true;
      });
      this.overlay = true;
      await this.setAnimationSteps();

      if (this.mobileBreakpoints()) {
        this.$emit('closeAllOtherServices', this.serviceCardId);
      }

      this.addActiveService({
        service_id: this.serviceCardId,
        service_type: 'RASTER',
        service_name: this.serviceName,
        layer_name: this.selectedRasterLayer.layer_name,
        layers: this.rasterLayers,
        daily_data: this.dailyData,
        is_table: this.isTable,
        is_graph: this.isGraph,
        is_animation: this.isAnimation,
        table_chart_range_date: this.tableChartRangeDate,
        animation_time_array: null,
        animation_start_index: this.animationStartIndex,
        animation_step_date_type: this.animationStepDateType,
        animation_empty_steps: this.animationEmptySteps,
        ws_name: this.selectedRasterLayer.ws_name,
        udm: this.selectedRasterLayer.udm,
        udm_conversions: this.selectedRasterLayer.udm_conversions,
        is_multi_variable: this.selectedRasterLayer.is_multi_variable,
        multi_variable_layers: this.selectedRasterLayer.multi_variable_layers || [],
        multi_variable_layer_configs: this.selectedRasterLayer.multi_variable_layer_configs || []
      });

      if (!this.isRefreshing && this.map.isAnimating.length == 0) {
        this.resetAnimationTime();
      }

      if (this.animationTimeArray.length > 0) {
        this.map.sliderOpacityValue = raster.layer_opacity;
        this.checkOpacityServiceID(this.serviceCardId);
        const previousRaster = this.map.OLmap.getLayers().getArray().find(layer => layer.values_.layer_type === 'RASTER' && layer.values_.service_id !== this.serviceCardId);

        if ((this.$route.params.id === 'Forecast' || this.$route.params.id === 'Real-Time') && previousRaster) {
          if (Object.keys(this.map.refreshIntervalID).includes(previousRaster.values_.service_id)) {
            this.clearRefreshInterval(previousRaster.values_.service_id);
          }
        }

        const extent = await this.getLayerExtent([raster.ws_name, raster.layer_name, raster.layer_type, raster.service_id]);
        const rasterParams = {
          url: `${BINARY_BASE_URL}/fe_ogc/geoserver/wms`,
          params: {
            ENV: '',
            GROUP: this.$route.params.id,
            LAYERS: `${raster.ws_name}:${raster.layer_name}`,
            TIME: this.animationTimeArray[this.animationStartIndex],
            STYLES: raster.layer_style,
            USERNAME: this.app_user.username,
            SERVICE_NAME: this.serviceName,
            FORMAT: 'image/vnd.jpeg-png8'
          },
          TYPE: 'RASTER',
          LAYER_SERVICE_ID: this.serviceCardId,
          UDM: raster.udm,
          UDM_CONVERSIONS: raster.udm_conversions,
          IS_MULTI_VARIABLE: raster.is_multi_variable,
          MULTI_VARIABLE_LAYERS: raster.multi_variable_layers || [],
          MULTI_VARIABLE_LAYER_CONFIGS: raster.multi_variable_layer_configs || [],
          EXTENT: extent
        };

        this.map.rasterMarkerDate = rasterParams.params.TIME;
        await this.loadRasterLayer(rasterParams);

        const activeService = this.wsServices.activeServices.filter(s => s.service_id === this.serviceCardId);

        if (activeService.length > 0) {
          activeService[0].animation_time_array = this.animationTimeArray;
          activeService[0].animation_start_index = this.animationStartIndex;
          this.wsServices.updateStep = true;
        }

        if (!isDefaultService) {
          if (this.app_user.defaultService.GLOBAL?.service_details && this.app_user.defaultService.GLOBAL?.service_details.length > 0) {
            const sliceIndex = this.app_user.defaultService.GLOBAL.service_details.findIndex(item => item.layer_type === 'RASTER');

            if (sliceIndex > -1) {
              if (this.map.layerToSelectRaster[this.app_user.defaultService.GLOBAL.service_details[sliceIndex].service_id]) {
                this.map.layerToSelectRaster[this.app_user.defaultService.GLOBAL.service_details[sliceIndex].service_id] = {};
              }

              this.app_user.defaultService.GLOBAL.service_details.splice(sliceIndex, 1);
            }
          }
        }

        if (!this.mobileBreakpoints()) {
          // chiude tutti i servizi raster
          this.$emit('closeRasterService', this.serviceCardId);
        }

        if (this.$route.params.id === 'Forecast') {
          this.renderPlayer = false;
          this.$nextTick(() => {
            this.renderPlayer = true;
          });
        }

        const unit = dateUnit(this.animationStepDateType);
        const centerCoordinates = toLonLat(this.map.OLmap.getView().getCenter());
        const timeStamp = Date.now();
        this.$gtag.event('Intervallo temporale', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id} - ${this.serviceName}`,
          event_label: `${timeStamp} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: `${getFormattedDate(new Date(rasterParams.params.TIME), true)} - MIN: ${getFormattedDate(new Date(this.minStep), true)} MAX: ${getFormattedDate(new Date(this.maxStep), true)} STEP: ${this.animationStep}${unit}`
        });
        this.$gtag.event('Centroide mappa', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id} - Lat ${centerCoordinates[1].toFixed(4)} - Lon ${centerCoordinates[0].toFixed(4)}`,
          event_label: `${timeStamp} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: `${this.serviceName}: ${this.selectedRasterLayer.layer_name} - ${getFormattedDate(new Date(rasterParams.params.TIME), true)}`
        });
      }

      this.overlay = false;

      if (!this.isRefreshing) {
        this.reloadLegend();
      }
    },
    reloadLegend() {
      this.map.reloadLegend = true;
      this.$nextTick(() => {
        this.map.reloadLegend = false;
      });
    },
    async removePreviousLayer() {
      if (this.map.OLmap.getLayers().getArray().slice(1).map(layer => layer.values_.service_id === this.serviceCardId)) {
        await this.removeActiveService(this.serviceCardId);
        await this.removeLayerByServiceID(this.serviceCardId);
      }
    },
    removeRasterSelected() {
      this.renderArchiveDate = false;
      this.$nextTick(() => {
        this.renderArchiveDate = true;
      });

      if (this.$route.params.id === 'Forecast' || this.$route.params.id === 'Real-Time') {
        if (Object.keys(this.map.refreshIntervalID).includes(this.selectedRasterLayer.service_id)) {
          this.clearRefreshInterval(this.selectedRasterLayer.service_id);
        }
      }

      this.selectedRasterLayer = {};
      this.removeEveryRasterLayer();
      this.reloadLegend();
    },
    async initRefresh() {
      this.isRefreshing = true;
      this.rotateEffect = 'rotating';
      this.refreshBtnDisabled = true;
      setTimeout(() => {
        this.rotateEffect = '';
        this.refreshBtnDisabled = false;
      }, 2000);

      this.$emit('set-refresh-interval-overlay', true);

      const currentLayer = {
        policy_id: this.selectedRasterLayer.policy_id,
        service_id: this.selectedRasterLayer.service_id,
        service_type: 'WS'
      };
      await this.storeWsServices(currentLayer);
      const hasNewAnimationTimestep =
        this.wsServices.wsAppUserPolicy[0].animation_step !== this.animationStep
        || this.wsServices.wsAppUserPolicy[0].animation_step_date_type !== this.animationStepDateType;

      if (hasNewAnimationTimestep) {
        this.$emit('updateWorkspace', this.wsServices.wsAppUserPolicy[0]);
      }

      if (this.animationTimeArray.length > 0) {
        if (this.policyLimits.type != 'static') {
          this.$emit('reset-min-max-step');
        }

        if (this.skipWmtsCalls) {
          if (hasNewAnimationTimestep) {
            await this.refreshRasterLayer();
          }

          this.geoserver_data.isNewData = [];
          this.geoserver_data.isRuntime = false;
          this.isRefreshing = false;
          this.$emit('set-refresh-interval-overlay', false);

          return;
        }

        await this.checkNewData(['time', `${this.selectedRasterLayer.ws_name}:${this.selectedRasterLayer.layer_name}`, this.animationTimeArray.at(-1)]);

        if (this.$route.params.id === 'Forecast') {
          this.runtime = await this.checkNewData(['RUNTIME', `${this.selectedRasterLayer.ws_name}:${this.selectedRasterLayer.layer_name}`, this.runtime]);
          this.map.runtime[this.selectedRasterLayer.service_id] = this.runtime;
        }

        const [isNewData, newDataTime] = this.geoserver_data.isNewData;
        const isRuntime = this.geoserver_data.isRuntime;

        if ((isNewData || isRuntime) && this.animationLimitDateType !== 'static') {
          this.$emit('changeAnimationLimitRangeArray');
          await this.$nextTick();
        }

        const limit = this.animationLimitRangeArrayPolicy.at(-1);

        if ((isNewData && new Date(newDataTime) <= new Date(limit)) || (this.$route.params.id === 'Forecast' && isRuntime)) {
          await this.refreshRasterLayer();
        } else if (hasNewAnimationTimestep) {
          await this.refreshRasterLayer();
        }
      } else {
        await this.refreshRasterLayer();
      }

      this.geoserver_data.isNewData = [];
      this.geoserver_data.isRuntime = false;
      this.isRefreshing = false;
      this.$emit('set-refresh-interval-overlay', false);
    },
    async refreshRasterLayer() {
      if (!this.map.isRefreshingDataServiceID.includes(this.selectedRasterLayer.service_id)) {
        this.map.isRefreshingDataServiceID.push(this.selectedRasterLayer.service_id);
      }

      if (this.map.isAnimating.includes(this.serviceCardId) && this.animationTimeArray.length > 0) {
        this.pauseAnimation = true;
        this.wsServices.pauseAnimation = true;
        await this.setAnimationSteps();
        this.pauseAnimation = false;
        this.wsServices.pauseAnimation = false;
      } else {
        await this.openRasterLayer(false, this.selectedRasterLayer);
      }

      this.map.isRefreshingDataServiceID = this.map.isRefreshingDataServiceID.filter(id => id !== this.selectedRasterLayer.service_id);
    },
    clearRefreshInterval(id) {
      clearInterval(this.map.refreshIntervalID[id]);
      delete this.map.refreshIntervalID[id];
    },
    setRefreshInterval() {
      const interval = timeToMilliseconds(this.refreshIntervalStep, this.refreshIntervalStepType);

      if (interval > 0) {
        this.map.refreshIntervalID[this.serviceCardId] = setInterval(async () => {
          await this.initRefresh();
        }, interval);
      }
    },
    setAnimationStepsWithoutWmts(wmtsTimeRange) {
      if (timeToMilliseconds(this.animationStep, this.animationStepDateType) <= 0) {
        this.animationTimeArray = [];

        return;
      }

      const [lowerBound, upperBound] = wmtsTimeRange.split('/');
      const animationTimeArray = [lowerBound];
      let dateToAdd = new Date(lowerBound).getTime();
      const limitDate = new Date(upperBound).getTime();

      while (dateToAdd < limitDate) {
        dateToAdd = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, this.$route.params.id, dateToAdd, true);

        if (dateToAdd <= limitDate) {
          animationTimeArray.push(new Date(dateToAdd).toISOString());
        }
      }

      this.tableChartRangeDate = wmtsTimeRange;
      this.animationStartIndex = 0;
      this.animationTimeArray = animationTimeArray;
      this.animationEmptySteps = [];

      const activeService = this.wsServices.activeServices.filter(s => s.service_id === this.serviceCardId);

      if (activeService.length > 0) {
        activeService[0].animation_time_array = this.animationTimeArray;
        activeService[0].animation_start_index = this.animationStartIndex;
        activeService[0].animation_empty_steps = this.animationEmptySteps;
        activeService[0].table_chart_range_date = this.tableChartRangeDate;
      }
    },
    async setAnimationSteps(userRangeDates) {
      const [archiveForecastInitialDate, archiveFinalDate] = userRangeDates || [];
      const isNewData = !userRangeDates;
      this.noWmtsFeaturesFound = this.wsServices.alertNoWMTSFeature = false;

      let tmpAnimationTimeArray = [];
      let wmtsTimeRange = '';
      const nowDate = new Date();
      const startTime = archiveForecastInitialDate || nowDate;
      const step = this.$route.params.id === 'Real-Time' ? this.animationLimitPast : this.animationLimitFuture;
      const otherBoundTime = this.$route.params.id === 'Archive' ? archiveFinalDate : updatedDateWithTimeStep(step, this.animationLimitDateType, this.$route.params.id, nowDate, true);

      wmtsTimeRange = this.$route.params.id === 'Real-Time'
        ? `${new Date(otherBoundTime).toISOString()}/${startTime.toISOString()}`
        : `${startTime.toISOString()}/${new Date(otherBoundTime).toISOString()}`;

      let minPolicy = this.animationLimitRangeArrayPolicy[0];
      let maxPolicy = this.animationLimitRangeArrayPolicy.at(-1);

      if (this.dailyData) {
        minPolicy = this.animationLimitRangeArrayPolicy[0] ? new Date(this.animationLimitRangeArrayPolicy[0]).setHours(0, 0, 0, 0) : null;
      }

      if (this.policyLimits.type !== 'static') {
        maxPolicy = updatedDateWithTimeStep(this.policyLimits.future, this.policyLimits.type, 'add_step', new Date(), true);
      }

      const lowerBoundPolicy = new Date(minPolicy) < new Date(wmtsTimeRange.split('/')[0])
        ? wmtsTimeRange.split('/')[0]
        : new Date(minPolicy).toISOString();

      const upperBoundPolicy = new Date(maxPolicy) > new Date(wmtsTimeRange.split('/')[1])
        ? wmtsTimeRange.split('/')[1]
        : new Date(maxPolicy).toISOString();

      wmtsTimeRange = `${lowerBoundPolicy}/${upperBoundPolicy}`;

      if (this.skipWmtsCalls) {
        this.setAnimationStepsWithoutWmts(wmtsTimeRange);

        return;
      }

      const wmtsParams = {
        layer: `${this.selectedRasterLayer.ws_name}:${this.selectedRasterLayer.layer_name}`,
        time: wmtsTimeRange
      };
      await this.getWmtsArray(wmtsParams);

      const wmtsResultArray = [];
      const xml_document = (new window.DOMParser()).parseFromString(this.geoserver_data.xml_string, 'text/xml');
      xml_document.getElementsByTagName('DimensionDomain').forEach(item => {
        if (item.getElementsByTagName('Domain')[0].previousElementSibling.textContent === 'time') {
          if (item.getElementsByTagName('Domain')[0].textContent !== '') {
            wmtsResultArray.push(item.getElementsByTagName('Domain')[0].textContent);
          }
        }

        if (item.getElementsByTagName('Domain')[0].previousElementSibling.textContent === 'RUNTIME') {
          if (this.$route.params.id === 'Forecast') {
            this.runtime = item.getElementsByTagName('Domain')[0].textContent.split('--')[1];
            this.map.runtime[this.selectedRasterLayer.service_id] = this.runtime;
          }

          this.table_chart.isRuntimeDomain[this.serviceCardId] = [true, this.animationStep, this.animationStepDateType];
        }
      });

      if (wmtsResultArray.length > 0) {
        const [lowerBound, upperBound] = wmtsResultArray[0].split('--');
        this.tableChartRangeDate = `${lowerBound}/${upperBound}`;

        if (this.$route.params.id === 'Real-Time') {
          tmpAnimationTimeArray.push(upperBound);
          let dateToAdd = new Date(upperBound).getTime();
          const limitDate = new Date(lowerBound).getTime();

          while (dateToAdd > limitDate) {
            dateToAdd = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, this.$route.params.id, dateToAdd, true);
            tmpAnimationTimeArray.push(new Date(dateToAdd).toISOString());
          }

          this.animationStartIndex = tmpAnimationTimeArray.length - 1;
          tmpAnimationTimeArray = tmpAnimationTimeArray.reverse();
        } else {
          // Forecast - Archive
          const tmpLowerBound = !isNewData || (lowerBound > this.animationTimeArray[0] || this.animationTimeArray.length <= 0)
            ? lowerBound
            : this.animationTimeArray[0];

          tmpAnimationTimeArray.push(tmpLowerBound);
          let dateToAdd = new Date(tmpLowerBound).getTime();
          const limitDate = new Date(upperBound).getTime();

          while (dateToAdd < limitDate) {
            dateToAdd = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, this.$route.params.id, dateToAdd, true);
            tmpAnimationTimeArray.push(new Date(dateToAdd).toISOString());
          }

          const lessStartIndex = this.animationStartIndex - 1;
          const tmpStartIndex = lessStartIndex >= 0 ? lessStartIndex : 0;
          this.animationStartIndex = isNewData ? 0 : tmpStartIndex;
        }

        this.animationTimeArray = tmpAnimationTimeArray;
        await this.getDataAvailability();
        const activeService = this.wsServices.activeServices.filter(s => s.service_id === this.serviceCardId);

        if (activeService.length > 0) {
          activeService[0].animation_time_array = this.animationTimeArray;
          activeService[0].animation_start_index = this.animationStartIndex;
          activeService[0].animation_empty_steps = this.animationEmptySteps;
          activeService[0].table_chart_range_date = this.tableChartRangeDate;
        }
      } else {
        if (this.$route.params.id === 'Forecast') {
          await this.removeLayerByServiceID(this.serviceCardId);
        }

        wmtsTimeRange = null;
        this.noWmtsFeaturesFound = this.wsServices.alertNoWMTSFeature = true;
        this.animationTimeArray = tmpAnimationTimeArray;
        this.removeActiveService(this.serviceCardId);
      }
    },
    async getDataAvailability() {
      const animationStepLength = this.animationTimeArray.length;

      if (animationStepLength > 0) {
        let time = new Date(this.animationTimeArray[0]);
        time = time.setTime(time.getTime() - 60000);

        await this.getWmtsGDV([`${this.selectedRasterLayer.ws_name}:${this.selectedRasterLayer.layer_name}`, new Date(time).toISOString(), this.$route.params.id === 'Forecast' ? undefined : animationStepLength]);

        const xml_document = (new window.DOMParser()).parseFromString(this.geoserver_data.xml_string_GDV, 'text/xml');
        const xml_content = xml_document.getElementsByTagName('Domain')[0].textContent;
        let wmtsResultArray = xml_content ? xml_content.split(',').sort() : [];
        const max = wmtsResultArray.find(time => new Date(time).getTime() > new Date(this.animationTimeArray[animationStepLength - 1]).getTime());

        if (max) {
          const index = wmtsResultArray.indexOf(max);
          wmtsResultArray = wmtsResultArray.slice(0, index);
        }

        this.animationEmptySteps = [];

        if (animationStepLength !== wmtsResultArray.length) {
          this.animationTimeArray.forEach(step => {
            if (!wmtsResultArray.some(time => time === step)) {
              this.animationEmptySteps.push(step);
            }
          });
        }

        this.noRasterFeaturesFound(this.animationEmptySteps.includes(this.animationTimeArray[this.animationStartIndex]));
      }
    },
    noRasterFeaturesFound(bool) {
      this.noFeaturesFound = bool;
      this.wsServices.alertNoFeature = bool;
    },
    toggleMapBtn() {
      this.mapEnabled = !this.mapEnabled;

      if (!this.mapEnabled) {
        this.removeRasterSelected();
        this.renderPlayer = false;
      }
    },
    googleTag() {
      if (this.selectedRasterLayer) {
        this.$gtag.event('Variabile', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id}`,
          event_label: `${Date.now()} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: `${this.serviceName}: ${this.selectedRasterLayer.layer_name}`
        });
      }
    },
    isSelectVoid() {
      return Object.keys(this.selectedRasterLayer).length === 0 || this.map.disableAnimationPlay || this.isRefreshing;
    },
    handleAppendIconClick() {
      if (!this.isSelectVoid()) {
        this.removeRasterSelected();
        this.removeActiveService(this.serviceCardId);
        this.renderPlayer = false;
      } else {
        this.$refs.rasterSelect.isMenuActive = true;
      }
    },
    getLayerInitialOpacity(value) {
      if (value === undefined || value === null) {
        return 60;
      }

      return value;
    },
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    }
  }
};
</script>

<style scoped>
.no-rotate-icon /deep/ .v-input__icon--append .v-icon {
  transition: none !important;
  transform: none !important;
}

@-webkit-keyframes rotating /* Safari and Chrome */ {
  from {
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes rotating {
  from {
    -ms-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -ms-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.rotating {
  -webkit-animation: rotating 1s linear infinite;
  -moz-animation: rotating 1s linear infinite;
  -ms-animation: rotating 1s linear infinite;
  -o-animation: rotating 1s linear infinite;
  animation: rotating 1s linear infinite;
}
</style>

